import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, Alert, Spin, Typography, Tooltip, Button, Modal, Tag, message } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useSelectedTimestamp } from '../../contexts/useSelectedTimestamp';
import { usePermissions } from '../../hooks/usePermissions';
import { Timeline } from '../../components/Timeline/Timeline';
import { DailyForecastView, HourlyForecastView, EditableTextModal, ForecastMarkdown, CollapsibleText } from '../../components/Forecast';
import forecastService, { type ForecastDaily, type ForecastHourly } from '../../api/forecastService';
import activityService, { type ActivityPeriod, computeActivityRanges } from '../../api/activityService';
import { getEventSource, isEventActive as checkEventActive } from '../../utils/eventSource';
import { useCollectionFreshness } from '../../hooks/useCollectionFreshness';

const { Title } = Typography;

// Modified indicator component - colored dot badge with blink animation
const ModifiedIndicator: React.FC = () => {
  const blinkStyle = `
    @keyframes blink-dot {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
  `;

  return (
    <>
      <style>{blinkStyle}</style>
      <Tooltip title="Modified from previous issued forecast">
        <span style={{
          display: 'inline-block',
          marginLeft: 6,
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#1890ff', // Ant Design primary blue
          cursor: 'help',
          verticalAlign: 'middle',
          animation: 'blink-dot 2s ease-in-out infinite',
        }} />
      </Tooltip>
    </>
  );
};

interface ForecastAvailability {
  daily?: Date[]; // Issuance times
  hourly?: { start: Date; end: Date }[]; // Coverage ranges
  hourlyIssuance?: Date[]; // Hourly forecast issuance times
}

// ActivityAvailability is now imported from activityService

/** Generate YYYY-MM-DD dates from start_date to end_date inclusive */
function getEventDateRange(startDate: string, endDate: string): string[] {
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const dates: string[] = [];
  const current = new Date(start);
  while (current <= end) {
    const y = current.getFullYear();
    const m = String(current.getMonth() + 1).padStart(2, '0');
    const d = String(current.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

const FORECAST_COMPACT_KEY = 'timeline-compact-forecast';

export const ForecastPage = () => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const { setSelectedTimestamp } = useSelectedTimestamp();
  const { isRoot } = usePermissions();
  // Check if observations are enabled (default to true for backward compatibility)
  const observationsEnabled = selectedEvent?.observations_enabled !== false;
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dailyForecasts, setDailyForecasts] = useState<ForecastDaily[]>([]);
  const [hourlyForecasts, setHourlyForecasts] = useState<ForecastHourly[]>([]);
  const [radarActivity, setRadarActivity] = useState<ActivityPeriod[]>([]);
  const [stationActivity, setStationActivity] = useState<ActivityPeriod[]>([]);
  const [forecastViewMode, setForecastViewMode] = useState<'cards' | 'table' | 'chart'>('cards');
  const [generalSituationModalOpen, setGeneralSituationModalOpen] = useState(false);
  const [selectedFilterDates, setSelectedFilterDates] = useState<string[]>([]);
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [timelineCompact, setTimelineCompact] = useState(() => {
    try { return localStorage.getItem(FORECAST_COMPACT_KEY) === 'true'; } catch { return false; }
  });
  const handleTimelineCompactChange = useCallback((v: boolean) => {
    setTimelineCompact(v);
    try { localStorage.setItem(FORECAST_COMPACT_KEY, String(v)); } catch { /* noop */ }
  }, []);

  // Event date range (start_date to end_date inclusive)
  const eventDateRange = useMemo(() => {
    if (!selectedEvent) return [];
    return getEventDateRange(selectedEvent.start_date, selectedEvent.end_date);
  }, [selectedEvent]);

  // Visible date range for the filter = dates the cards actually show (selected date and forward)
  // Cards only render current day and forward, so the filter options should match
  const visibleDateRange = useMemo(() => {
    if (eventDateRange.length === 0) return [];
    if (!selectedDate) return eventDateRange;
    const timezone = selectedEvent?.circuit?.time_zone || 'UTC';
    const dateStr = selectedDate.toLocaleDateString('sv-SE', { timeZone: timezone });
    // Include selected date and all after it
    const startIdx = eventDateRange.indexOf(dateStr);
    if (startIdx < 0) return eventDateRange; // selected date outside range, show all
    return eventDateRange.slice(startIdx);
  }, [eventDateRange, selectedDate, selectedEvent?.circuit?.time_zone]);

  // Reset date filter when event changes
  useEffect(() => {
    if (eventDateRange.length > 0) {
      setSelectedFilterDates([...eventDateRange]);
    } else {
      setSelectedFilterDates([]);
    }
  }, [eventDateRange]);

  // When timeline moves and filter is enabled, sync so selected dates stay within visible range
  useEffect(() => {
    if (!dateFilterEnabled || visibleDateRange.length === 0) return;
    setSelectedFilterDates(prev => {
      const kept = prev.filter(d => visibleDateRange.includes(d));
      return kept.length > 0 ? kept : [...visibleDateRange];
    });
  }, [dateFilterEnabled, visibleDateRange]);

  // Ref to track if we're currently fetching to prevent duplicate requests
  const fetchingRef = useRef(false);
  const lastEventKeyRef = useRef<string>('');
  // Ref to track the activity refresh interval
  const activityRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check if the event is currently active (for live updates)
  // Uses circuit timezone for consistent behavior across the app
  const isEventActive = checkEventActive(selectedEvent);

  // Poll metadata timestamps to detect external forecast updates
  const metadataTimestamps = useCollectionFreshness(['forecast_daily', 'forecast_hourly'], {
    interval: 30_000,
    enabled: isEventActive,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastFetchTimeRef = useRef<string>('');
  const prevRefreshCounterRef = useRef(0);

  useEffect(() => {
    const latest = metadataTimestamps.forecast_daily || metadataTimestamps.forecast_hourly;
    if (latest && latest > lastFetchTimeRef.current) {
      lastFetchTimeRef.current = latest;
      setRefreshCounter(c => c + 1);
    }
  }, [metadataTimestamps]);

  // Root can edit active events; demo events are always editable for testing
  const isForecastEditable = isRoot && (isEventActive || !!selectedEvent?.demo);

  const eventSource = selectedEvent ? getEventSource(selectedEvent) : null;
  const showStatusControls = isRoot && selectedEvent != null && eventSource !== 'legacy';

  // Function to refresh activity data (used on interval for live events)
  const refreshActivityData = useCallback(async () => {
    if (!selectedEvent || !isEventActive || !observationsEnabled) return;

    try {
      const source = getEventSource(selectedEvent);

      // Fetch both radar and station activity in parallel
      const radarSource = source === 'legacy' ? 'legacy' : 'l2';
      const [radarResponse, stationResponse] = await Promise.allSettled([
        activityService.getRadarActivity({
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          source: radarSource,
        }),
        activityService.getStationActivity({
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          source,
        }),
      ]);

      if (radarResponse.status === 'fulfilled' && radarResponse.value.activities.length > 0) {
        setRadarActivity(radarResponse.value.activities);
        if (import.meta.env.DEV) {
          console.log('[Forecast] Refreshed radar activity');
        }
      }

      if (stationResponse.status === 'fulfilled' && stationResponse.value.activities.length > 0) {
        setStationActivity(stationResponse.value.activities);
        if (import.meta.env.DEV) {
          console.log('[Forecast] Refreshed station activity');
        }
      }
    } catch (err) {
      console.error('[Forecast] Error refreshing activity data:', err);
    }
  }, [selectedEvent, isEventActive, observationsEnabled]);

  // Set up 5-minute interval to refresh activity data for live events
  // NOTE: We intentionally do NOT refresh on visibility/focus changes anymore.
  // This was causing bad UX where the timeline would refresh unnecessarily when
  // switching browser tabs. Instead, we use a periodic interval for live events.
  useEffect(() => {
    // Clear any existing interval
    if (activityRefreshIntervalRef.current) {
      clearInterval(activityRefreshIntervalRef.current);
      activityRefreshIntervalRef.current = null;
    }

    // Only set up interval for active events with observations enabled
    if (!isEventActive || !observationsEnabled) {
      return;
    }

    // Refresh activity data every 5 minutes for live events.
    // In dev, React StrictMode runs mount → cleanup → mount, so setup runs twice (only one interval stays active).
    const ACTIVITY_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
    activityRefreshIntervalRef.current = setInterval(() => {
      refreshActivityData();
    }, ACTIVITY_REFRESH_INTERVAL);

    return () => {
      if (activityRefreshIntervalRef.current) {
        clearInterval(activityRefreshIntervalRef.current);
        activityRefreshIntervalRef.current = null;
      }
    };
  }, [isEventActive, observationsEnabled, refreshActivityData]);

  // Fetch forecasts and activity data when event is selected
  useEffect(() => {
    if (!selectedEvent) {
      setDailyForecasts([]);
      setHourlyForecasts([]);
      setRadarActivity([]);
      setStationActivity([]);
      setSelectedDate(null);
      setSelectedTimestamp(null);
      lastEventKeyRef.current = '';
      return;
    }

    // Create a unique key for the current event
    const eventKey = `${selectedEvent.season_year}-${selectedEvent.circuit_id}-${selectedEvent.start_date}`;

    // If we're already fetching this event, skip
    if (fetchingRef.current && lastEventKeyRef.current === eventKey) {
      return;
    }

    // Detect if this re-run was triggered by a metadata refresh
    const isRefreshTriggered = refreshCounter !== prevRefreshCounterRef.current;
    prevRefreshCounterRef.current = refreshCounter;

    // If the event hasn't changed and we have data, skip fetching
    // (unless triggered by a metadata refresh)
    if (!isRefreshTriggered && lastEventKeyRef.current === eventKey && (dailyForecasts.length > 0 || hourlyForecasts.length > 0)) {
      return;
    }

    const fetchData = async () => {
      // Prevent concurrent fetches
      if (fetchingRef.current) {
        return;
      }

      fetchingRef.current = true;
      lastEventKeyRef.current = eventKey;
      setLoading(true);
      setError(null);

      try {
        // Determine source based on event type
        const source = getEventSource(selectedEvent);

        // Only fetch radar and station activity if observations are enabled
        const radarSource = source === 'legacy' ? 'legacy' : 'l2';
        const activityPromises: Promise<Awaited<ReturnType<typeof activityService.getRadarActivity>>>[] = observationsEnabled ? [
          activityService.getRadarActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source: radarSource,
          }),
          activityService.getStationActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
        ] : [
          Promise.resolve({ success: true, count: 0, activities: [] }),
          Promise.resolve({ success: true, count: 0, activities: [] }),
        ];

        // Try to fetch forecasts and activity data
        const includeDeleted = isRoot && source !== 'legacy';

        const [dailyResponse, hourlyResponse, radarResponse, stationResponse] = await Promise.allSettled([
          forecastService.getForecastDaily({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
            ...(includeDeleted && { include_deleted: true }),
          }),
          forecastService.getForecastHourly({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
            ...(includeDeleted && { include_deleted: true }),
          }),
          ...activityPromises,
        ]);

        const hasDailyData = dailyResponse.status === 'fulfilled' && dailyResponse.value.forecasts.length > 0;
        const hasHourlyData = hourlyResponse.status === 'fulfilled' && hourlyResponse.value.forecasts.length > 0;

        if (hasDailyData) {
          setDailyForecasts(dailyResponse.value.forecasts);
        }

        if (hasHourlyData) {
          setHourlyForecasts(hourlyResponse.value.forecasts);
        }

        // Set radar activity data (only if observations enabled)
        if (observationsEnabled) {
          if (radarResponse.status === 'fulfilled' && radarResponse.value.activities.length > 0) {
            setRadarActivity(radarResponse.value.activities);
          } else {
            setRadarActivity([]);
          }

          // Set station activity data
          if (stationResponse.status === 'fulfilled' && stationResponse.value.activities.length > 0) {
            setStationActivity(stationResponse.value.activities);
          } else {
            setStationActivity([]);
          }
        } else {
          // Clear activity when observations are disabled
          setRadarActivity([]);
          setStationActivity([]);
        }

        // Note: If no forecast data is available, we don't treat it as an error.
        // The render logic will show an appropriate info message.
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load forecast data');
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, observationsEnabled, refreshCounter]);

  const handleDatePointChange = useCallback((date: Date) => {
    setSelectedDate(date);
    setSelectedTimestamp(date);
  }, [setSelectedTimestamp]);

  // Normalize value_date for comparison (handles ISO format differences from API)
  const valueDatesMatch = useCallback((a: string, b: string) => {
    try {
      return new Date(a).getTime() === new Date(b).getTime();
    } catch {
      return a === b;
    }
  }, []);

  // Handler for saving previous night text (root users only)
  // dateKey (YYYY-MM-DD) is optional - when provided, also updates daily_metadata for that day
  const handleSavePreviousNight = useCallback(async (valueDate: string, newText: string, dateKey?: string) => {
    if (!selectedEvent) {
      throw new Error('No event selected');
    }

    const source = getEventSource(selectedEvent);

    await forecastService.updateForecastHourlyText({
      season_year: selectedEvent.season_year,
      circuit_id: selectedEvent.circuit_id,
      start_date: selectedEvent.start_date,
      value_date: valueDate,
      previous_night: newText,
      ...(dateKey && { date: dateKey }),
      source,
    });

    // Update local state: root-level and daily_metadata when present
    setHourlyForecasts(prev => prev.map(f => {
      if (!valueDatesMatch(f.value_date, valueDate)) return f;
      const updated = { ...f, previous_night: newText };
      if (dateKey && f.daily_metadata?.length) {
        updated.daily_metadata = f.daily_metadata.map(dm =>
          dm.date === dateKey ? { ...dm, previous_night: newText } : dm
        );
      }
      return updated;
    }));
  }, [selectedEvent, valueDatesMatch]);

  // Handler for saving weather summary text (root users only)
  // dateKey (YYYY-MM-DD) is optional - when provided, also updates daily_metadata for that day
  const handleSaveWeatherSummary = useCallback(async (valueDate: string, newText: string, dateKey?: string) => {
    if (!selectedEvent) {
      throw new Error('No event selected');
    }

    const source = getEventSource(selectedEvent);

    await forecastService.updateForecastHourlyText({
      season_year: selectedEvent.season_year,
      circuit_id: selectedEvent.circuit_id,
      start_date: selectedEvent.start_date,
      value_date: valueDate,
      weather_summary: newText,
      ...(dateKey && { date: dateKey }),
      source,
    });

    // Update local state: root-level and daily_metadata when present
    setHourlyForecasts(prev => prev.map(f => {
      if (!valueDatesMatch(f.value_date, valueDate)) return f;
      const updated = { ...f, weather_summary: newText };
      if (dateKey && f.daily_metadata?.length) {
        updated.daily_metadata = f.daily_metadata.map(dm =>
          dm.date === dateKey ? { ...dm, weather_summary: newText } : dm
        );
      }
      return updated;
    }));
  }, [selectedEvent, valueDatesMatch]);

  // Handler for saving individual parameter values in hourly forecast cards (root users only)
  const handleSaveParameter = useCallback(async (valueDate: string, forecastDatetime: string, paramName: string, newValue: string) => {
    if (!selectedEvent) {
      throw new Error('No event selected');
    }

    const source = getEventSource(selectedEvent);

    const result = await forecastService.updateForecastHourlyParameters({
      season_year: selectedEvent.season_year,
      circuit_id: selectedEvent.circuit_id,
      start_date: selectedEvent.start_date,
      value_date: valueDate,
      updates: [{ forecast_datetime: forecastDatetime, name: paramName, value: newValue }],
      source,
    });

    const appliedUpdates = result.applied_updates ?? [
      { forecast_datetime: forecastDatetime, name: paramName, value: newValue },
    ];

    // Update local state: patch all applied parameter values (including derived heat_index)
    setHourlyForecasts(prev => prev.map(f => {
      if (!valueDatesMatch(f.value_date, valueDate)) return f;
      return {
        ...f,
        hourly_forecasts: f.hourly_forecasts.map(hf => {
          const hfDatetime = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
            ? (hf.forecast_datetime as { value: string }).value
            : hf.forecast_datetime;

          const slotUpdates = appliedUpdates.filter(u => valueDatesMatch(hfDatetime, u.forecast_datetime));
          if (slotUpdates.length === 0) return hf;

          return {
            ...hf,
            parameters: hf.parameters.map(p => {
              const update = slotUpdates.find(u => u.name === p.name);
              return update ? { ...p, value: update.value || null } : p;
            }),
          };
        }),
      };
    }));
  }, [selectedEvent, valueDatesMatch]);

  // Handler for saving individual parameter values in daily forecast cards (root users only)
  const handleSaveDailyParameter = useCallback(async (valueDate: string, forecastDate: string, paramName: string, newValue: string) => {
    if (!selectedEvent) {
      throw new Error('No event selected');
    }

    const source = getEventSource(selectedEvent);

    const result = await forecastService.updateForecastDailyParameters({
      season_year: selectedEvent.season_year,
      circuit_id: selectedEvent.circuit_id,
      start_date: selectedEvent.start_date,
      value_date: valueDate,
      updates: [{ forecast_date: forecastDate, name: paramName, value: newValue }],
      source,
    });

    const appliedUpdates = result.applied_updates ?? [
      { forecast_date: forecastDate, name: paramName, value: newValue },
    ];

    // Update local state: patch all applied parameter values (including derived heat_index)
    setDailyForecasts(prev => prev.map(f => {
      if (!valueDatesMatch(f.value_date, valueDate)) return f;
      return {
        ...f,
        daily_forecasts: f.daily_forecasts.map(df => {
          const dfDate = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
            ? (df.forecast_date as { value: string }).value
            : df.forecast_date;

          const slotUpdates = appliedUpdates.filter(u => dfDate === u.forecast_date);
          if (slotUpdates.length === 0) return df;

          return {
            ...df,
            parameters: df.parameters.map(p => {
              const update = slotUpdates.find(u => u.name === p.name);
              return update ? { ...p, value: update.value || null } : p;
            }),
          };
        }),
      };
    }));
  }, [selectedEvent, valueDatesMatch]);

  // Calculate forecast issuance times and coverage ranges
  const forecastAvailability = useMemo<ForecastAvailability>(() => {
    const availability: ForecastAvailability = {};

    // Extract daily forecast issuance times (value_date = when forecast was created)
    if (dailyForecasts.length > 0) {
      availability.daily = dailyForecasts.map(f => new Date(f.value_date));
    }

    // Extract hourly forecast issuance times (value_date = when forecast was created)
    if (hourlyForecasts.length > 0) {
      availability.hourlyIssuance = hourlyForecasts.map(f => new Date(f.value_date));
    }

    // Extract hourly forecast coverage ranges (one range per forecast issuance)
    // Each forecast gets its own bar showing what period it covers
    if (hourlyForecasts.length > 0) {
      const ranges: { start: Date; end: Date }[] = [];

      hourlyForecasts.forEach(forecast => {
        if (forecast.hourly_forecasts && forecast.hourly_forecasts.length > 0) {
          const forecastTimes: number[] = [];

          forecast.hourly_forecasts.forEach(hf => {
            // Extract the datetime value (can be string or {value: string})
            const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
              ? (hf.forecast_datetime as { value: string }).value
              : hf.forecast_datetime;

            if (datetimeValue) {
              forecastTimes.push(new Date(datetimeValue).getTime());
            }
          });

          // For THIS forecast, create a bar from first to last hour
          if (forecastTimes.length > 0) {
            const minTime = Math.min(...forecastTimes);
            const maxTime = Math.max(...forecastTimes);
            ranges.push({
              start: new Date(minTime),
              end: new Date(maxTime)
            });
          }
        }
      });

      availability.hourly = ranges;
    }

    return availability;
  }, [dailyForecasts, hourlyForecasts]);

  // Calculate radar activity availability ranges (only if observations enabled)
  const radarActivityAvailability = useMemo(
    () => {
      if (!observationsEnabled || radarActivity.length === 0) return undefined;
      return computeActivityRanges(radarActivity);
    },
    [radarActivity, observationsEnabled]
  );

  // Calculate station activity availability ranges (only if observations enabled)
  const stationActivityAvailability = useMemo(
    () => {
      if (!observationsEnabled || stationActivity.length === 0) return undefined;
      return computeActivityRanges(stationActivity);
    },
    [stationActivity, observationsEnabled]
  );

  // Filter forecasts by selected dates — only when date filter is enabled (avoids perf overhead when off)
  const selectedFilterDatesSet = useMemo(
    () => new Set(selectedFilterDates),
    [selectedFilterDates]
  );

  const filteredDailyForecasts = useMemo((): ForecastDaily[] => {
    if (!dateFilterEnabled) return dailyForecasts;
    if (selectedFilterDates.length === 0) return [];
    // When all visible dates are selected, skip filtering (view applies selected-date logic)
    if (visibleDateRange.length > 0 && selectedFilterDates.length === visibleDateRange.length) return dailyForecasts;
    return dailyForecasts
      .map(f => ({
        ...f,
        daily_forecasts: f.daily_forecasts.filter(df => {
          const dateValue = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
            ? (df.forecast_date as { value: string }).value
            : df.forecast_date;
          return selectedFilterDatesSet.has(dateValue);
        }),
      }))
      .filter(f => f.daily_forecasts.length > 0);
  }, [dateFilterEnabled, dailyForecasts, selectedFilterDates, selectedFilterDatesSet, visibleDateRange.length]);

  const filteredHourlyForecasts = useMemo((): ForecastHourly[] => {
    if (!dateFilterEnabled) return hourlyForecasts;
    if (selectedFilterDates.length === 0) return [];
    // When all visible dates are selected, skip filtering (view applies selected-date logic)
    if (visibleDateRange.length > 0 && selectedFilterDates.length === visibleDateRange.length) return hourlyForecasts;
    const timezone = selectedEvent?.circuit?.time_zone || 'UTC';
    return hourlyForecasts
      .map(f => ({
        ...f,
        hourly_forecasts: f.hourly_forecasts.filter(hf => {
          const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
            ? (hf.forecast_datetime as { value: string }).value
            : hf.forecast_datetime;
          if (!datetimeValue) return false;
          const dateStr = new Date(datetimeValue).toLocaleDateString('sv-SE', { timeZone: timezone });
          return selectedFilterDatesSet.has(dateStr);
        }),
      }))
      .filter(f => f.hourly_forecasts.length > 0);
  }, [dateFilterEnabled, hourlyForecasts, selectedFilterDates, selectedFilterDatesSet, visibleDateRange.length, selectedEvent?.circuit?.time_zone]);

  // Determine which forecast type to show based on which was most recently ISSUED before selected time
  // Process: First daily forecasts are generated, then later hourly forecasts are generated
  // We should show the latest forecast (daily OR hourly) that was issued before the selected time
  const currentForecastType = useMemo((): 'hourly' | 'daily' | 'none' => {
    if (!selectedDate) {
      return 'none';
    }

    const selectedTime = selectedDate.getTime();

    // Find the most recent daily forecast issued before selected time
    let latestDailyIssueTime: number | null = null;
    if (dailyForecasts.length > 0) {
      const validDailyForecasts = dailyForecasts.filter(f => {
        const issueTime = new Date(f.value_date).getTime();
        return issueTime <= selectedTime;
      });

      if (validDailyForecasts.length > 0) {
        latestDailyIssueTime = Math.max(...validDailyForecasts.map(f => new Date(f.value_date).getTime()));
      }
    }

    // Find the most recent hourly forecast issued before selected time
    let latestHourlyIssueTime: number | null = null;
    if (hourlyForecasts.length > 0) {
      const validHourlyForecasts = hourlyForecasts.filter(f => {
        const issueTime = new Date(f.value_date).getTime();
        return issueTime <= selectedTime;
      });

      if (validHourlyForecasts.length > 0) {
        latestHourlyIssueTime = Math.max(...validHourlyForecasts.map(f => new Date(f.value_date).getTime()));
      }
    }

    // No forecasts issued before selected time
    if (latestDailyIssueTime === null && latestHourlyIssueTime === null) {
      return 'none';
    }

    // Only daily forecast available
    if (latestDailyIssueTime !== null && latestHourlyIssueTime === null) {
      return 'daily';
    }

    // Only hourly forecast available
    if (latestHourlyIssueTime !== null && latestDailyIssueTime === null) {
      return 'hourly';
    }

    // Both available - show the one that was issued most recently
    if (latestHourlyIssueTime! >= latestDailyIssueTime!) {
      return 'hourly';
    }

    return 'daily';
  }, [selectedDate, dailyForecasts, hourlyForecasts]);

  // Get the active daily forecast (latest issued before selected time) for general situation
  // This is shown regardless of whether we're displaying daily or hourly view,
  // since general situation provides context for both
  const activeDailyForecast = useMemo(() => {
    if (dailyForecasts.length === 0 || !selectedDate) return null;

    const selectedTime = selectedDate.getTime();

    // Filter forecasts issued before the selected time
    const validForecasts = dailyForecasts.filter(f => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime <= selectedTime;
    });

    // If no forecasts were issued before the selected time, return null
    if (validForecasts.length === 0) {
      return null;
    }

    // Sort by value_date descending and take the most recent one before selected time
    const sorted = validForecasts.sort((a, b) => {
      return new Date(b.value_date).getTime() - new Date(a.value_date).getTime();
    });

    const activeForecast = sorted[0];

    // Check if selected date is within the forecast range (using calendar dates in UTC to avoid timezone issues)
    if (activeForecast.daily_forecasts) {
      // Extract calendar date from selectedDate in the circuit timezone
      const timezone = selectedEvent?.circuit?.time_zone;
      const dateStr = selectedDate.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      // Parse "MM/DD/YYYY" format and create UTC date for comparison
      const [month, day, year] = dateStr.split('/').map(Number);
      const selectedCalendarDate = Date.UTC(year, month - 1, day);

      // Get calendar dates from all forecast entries (also as UTC)
      const forecastCalendarDates = activeForecast.daily_forecasts.map(df => {
        const dateValue = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
          ? (df.forecast_date as { value: string }).value
          : df.forecast_date;
        const [year, month, day] = dateValue.split('-').map(Number);
        return Date.UTC(year, month - 1, day);
      });

      const minDate = Math.min(...forecastCalendarDates);
      const maxDate = Math.max(...forecastCalendarDates);

      // Only show general situation if selected date is within range
      if (selectedCalendarDate < minDate || selectedCalendarDate > maxDate) {
        return null;
      }
    }

    return activeForecast;
  }, [dailyForecasts, selectedDate, selectedEvent]);

  // Handler for saving general situation text (root users only)
  // Placed after activeDailyForecast useMemo to avoid "used before declaration" error
  const handleSaveGeneralSituation = useCallback(async (newText: string) => {
    if (!selectedEvent || !activeDailyForecast) {
      throw new Error('No event or forecast selected');
    }

    const source = getEventSource(selectedEvent);

    await forecastService.updateForecastDailyText({
      season_year: selectedEvent.season_year,
      circuit_id: selectedEvent.circuit_id,
      start_date: selectedEvent.start_date,
      value_date: activeDailyForecast.value_date,
      general_situation: newText,
      source,
    });

    // Update local state to reflect the change (use timestamp comparison for format robustness)
    setDailyForecasts(prev => prev.map(f =>
      valueDatesMatch(f.value_date, activeDailyForecast.value_date)
        ? { ...f, general_situation: newText }
        : f
    ));
  }, [selectedEvent, activeDailyForecast, valueDatesMatch]);

  // Get the active hourly forecast (latest issued before selected time)
  const activeHourlyForecast = useMemo(() => {
    if (hourlyForecasts.length === 0 || !selectedDate) return null;
    const selectedTime = selectedDate.getTime();
    const validForecasts = hourlyForecasts.filter(f => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime <= selectedTime;
    });
    if (validForecasts.length === 0) return null;
    return validForecasts.sort((a, b) =>
      new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
    )[0];
  }, [hourlyForecasts, selectedDate]);

  // The currently displayed forecast for status badge purposes
  const activeForecast: ForecastDaily | ForecastHourly | null =
    currentForecastType === 'daily' ? activeDailyForecast :
    currentForecastType === 'hourly' ? activeHourlyForecast : null;

  // Handler for saving weather description text for daily forecasts (root users only)
  const handleSaveWeatherDescription = useCallback(async (valueDate: string, forecastDate: string, newText: string) => {
    if (!selectedEvent) {
      throw new Error('No event selected');
    }

    const source = getEventSource(selectedEvent);

    await forecastService.updateForecastDailyText({
      season_year: selectedEvent.season_year,
      circuit_id: selectedEvent.circuit_id,
      start_date: selectedEvent.start_date,
      value_date: valueDate,
      forecast_date: forecastDate,
      weather_description: newText,
      source,
    });

    // Update local state to reflect the change (use timestamp comparison for format robustness)
    setDailyForecasts(prev => prev.map(f => {
      if (valueDatesMatch(f.value_date, valueDate)) {
        return {
          ...f,
          daily_forecasts: f.daily_forecasts.map(df => {
            const dfDate = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
              ? (df.forecast_date as { value: string }).value
              : df.forecast_date;
            if (dfDate === forecastDate) {
              return { ...df, weather_description: newText };
            }
            return df;
          }),
        };
      }
      return f;
    }));
  }, [selectedEvent, valueDatesMatch]);

  // Handler for toggling forecast publish status (root users only, non-legacy events)
  const handleStatusToggle = useCallback(async () => {
    if (!selectedEvent || !activeForecast || currentForecastType === 'none') return;

    const currentStatus = activeForecast.status ?? 'published';
    const newStatus: 'draft' | 'published' = currentStatus === 'draft' ? 'published' : 'draft';
    const type = currentForecastType as 'daily' | 'hourly';
    const source = getEventSource(selectedEvent);

    const performUpdate = async () => {
      setStatusUpdating(true);
      try {
        await forecastService.updateForecastStatus(type, {
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          value_date: activeForecast.value_date,
          create_datetime: activeForecast.create_datetime,
          status: newStatus,
          source,
        });

        const updateStatus = (f: ForecastDaily | ForecastHourly) =>
          valueDatesMatch(f.value_date, activeForecast.value_date) && f.create_datetime === activeForecast.create_datetime
            ? { ...f, status: newStatus }
            : f;

        if (type === 'daily') {
          setDailyForecasts(prev => prev.map(f => updateStatus(f) as ForecastDaily));
        } else {
          setHourlyForecasts(prev => prev.map(f => updateStatus(f) as ForecastHourly));
        }

        message.success(`Forecast ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`);
      } catch (err) {
        console.error('Failed to update forecast status:', err);
        message.error(`Failed to ${newStatus === 'published' ? 'publish' : 'unpublish'} forecast`);
      } finally {
        setStatusUpdating(false);
      }
    };

    if (newStatus === 'draft') {
      Modal.confirm({
        title: 'Unpublish Forecast',
        content: 'This will set the forecast back to Draft. Non-root users will no longer see it. Continue?',
        okText: 'Unpublish',
        okType: 'danger',
        onOk: performUpdate,
      });
    } else {
      await performUpdate();
    }
  }, [selectedEvent, activeForecast, currentForecastType, valueDatesMatch]);

  const statusExtra = showStatusControls && activeForecast && currentForecastType !== 'none' ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tag
        color={activeForecast.status === 'draft' ? 'orange' : 'green'}
        style={{
          margin: 0,
          fontWeight: activeForecast.status === 'draft' ? 600 : 400,
        }}
      >
        {activeForecast.status === 'draft' ? 'Draft' : 'Published'}
      </Tag>
      {activeForecast.status === 'draft' ? (
        <Button type="primary" size="small" loading={statusUpdating} onClick={handleStatusToggle}>
          Publish
        </Button>
      ) : (
        <Button size="small" loading={statusUpdating} onClick={handleStatusToggle}>
          Unpublish
        </Button>
      )}
    </div>
  ) : null;

  // Render the appropriate forecast view based on what's available
  const renderForecastView = () => {
    if (loading) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        </Card>
      );
    }

    if (error) {
      return (
        <Card>
          <Alert title="Error Loading Forecast Data" description={<div><p>{error}</p></div>} type="error" showIcon />
        </Card>
      );
    }

    // Show forecast based on current selection (not global mode)
    if (currentForecastType === 'hourly' && filteredHourlyForecasts.length > 0) {
      return (
        <HourlyForecastView
          forecasts={filteredHourlyForecasts}
          selectedDate={selectedDate}
          viewMode={forecastViewMode}
          onViewModeChange={setForecastViewMode}
          isEditable={isForecastEditable}
          onSavePreviousNight={handleSavePreviousNight}
          onSaveWeatherSummary={handleSaveWeatherSummary}
          onSaveParameter={handleSaveParameter}
          dateFilterEnabled={dateFilterEnabled}
          onDateFilterEnabledChange={setDateFilterEnabled}
          eventDateRange={visibleDateRange}
          selectedFilterDates={selectedFilterDates}
          onFilterDatesChange={setSelectedFilterDates}
          statusExtra={statusExtra}
        />
      );
    }

    if (currentForecastType === 'daily' && filteredDailyForecasts.length > 0) {
      return (
        <DailyForecastView
          forecasts={filteredDailyForecasts}
          selectedDate={selectedDate}
          showGeneralSituation={false}
          viewMode={forecastViewMode}
          onViewModeChange={setForecastViewMode}
          isEditable={isForecastEditable}
          onSaveWeatherDescription={handleSaveWeatherDescription}
          onSaveParameter={handleSaveDailyParameter}
          dateFilterEnabled={dateFilterEnabled}
          onDateFilterEnabledChange={setDateFilterEnabled}
          eventDateRange={visibleDateRange}
          selectedFilterDates={selectedFilterDates}
          onFilterDatesChange={setSelectedFilterDates}
          statusExtra={statusExtra}
        />
      );
    }

    return (
      <Card>
        <Alert
          title="No Forecast Data"
          description={
            <div><p>No forecast data available for this event.</p></div>
          }
          type="info"
          showIcon
        />
      </Card>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Sticky Header */}
      {/* <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: theme === 'dark' ? '#141414' : '#f0f2f5',
        zIndex: 10,
        paddingBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}><CloudOutlined /> Forecast</h1>
          <span style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)' }}>
            Weather forecast data and predictions for race events.
          </span>
        </div>
      </div> */}

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selectedEvent ? (
          <Card>
            <Alert
              title="No Event Selected"
              description="Please select an event to view forecast data."
              type="info"
              showIcon
            />
          </Card>
        ) : (
          <>
            {/* Timeline - Fixed, not scrollable */}
            <div style={{
              flexShrink: 0,
            }}>
              <Timeline
                mode="point"
                onDatePointChange={handleDatePointChange}
                forecastAvailability={forecastAvailability}
                radarActivityAvailability={radarActivityAvailability}
                stationActivityAvailability={stationActivityAvailability}
                compact={timelineCompact}
                onCompactChange={handleTimelineCompactChange}
              />
            </div>

            {/* General Situation - Fixed, not scrollable, collapsed by default */}
            {activeDailyForecast?.general_situation && (
              <Card
                style={{
                  margin: '0 0 0 0',
                  backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fafafa',
                  borderColor: theme === 'dark' ? '#434343' : '#d9d9d9',
                  borderRadius: 0,
                }}
              >
                <CollapsibleText
                  content={activeDailyForecast.general_situation}
                  isDark={theme === 'dark'}
                  compact
                  defaultCollapsed
                  header={<>
                    <Title level={5} style={{ marginTop: 0, marginBottom: 0, display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                      General Situation
                      {activeDailyForecast.general_situation_modified === 1 && <ModifiedIndicator />}
                    </Title>
                    {isForecastEditable && (
                      <EditableTextModal
                        title="General Situation"
                        initialValue={activeDailyForecast.general_situation}
                        onSave={handleSaveGeneralSituation}
                      />
                    )}
                    <Tooltip title="View full details">
                      <Button
                        type="text"
                        size="small"
                        icon={<FullscreenOutlined />}
                        onClick={() => setGeneralSituationModalOpen(true)}
                        style={{ padding: '0 4px', height: 'auto', display: 'flex', alignItems: 'center' }}
                      />
                    </Tooltip>
                  </>}
                />
              </Card>
            )}

            {/* General Situation Modal */}
            {activeDailyForecast?.general_situation && (
              <Modal
                title="General Situation"
                open={generalSituationModalOpen}
                onCancel={() => setGeneralSituationModalOpen(false)}
                footer={[
                  <Button key="close" onClick={() => setGeneralSituationModalOpen(false)}>
                    Close
                  </Button>,
                ]}
                width={800}
                styles={{
                  body: {
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    padding: '24px',
                  },
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  {isForecastEditable && (
                    <EditableTextModal
                      title="General Situation"
                      initialValue={activeDailyForecast.general_situation}
                      onSave={handleSaveGeneralSituation}
                      buttonSize="small"
                    />
                  )}
                </div>
                <div
                  style={{
                    marginBottom: 0,
                    wordWrap: 'break-word',
                    padding: '12px',
                    backgroundColor: theme === 'dark' ? '#141414' : '#f5f5f5',
                    borderRadius: 4,
                  }}
                >
                  <ForecastMarkdown source={activeDailyForecast.general_situation} isDark={theme === 'dark'} />
                </div>
              </Modal>
            )}

            {/* Forecast View - Scrollable area */}
            <div style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              padding: 0,
            }}>
              {renderForecastView()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

